import {
    audioAttachmentMediaKey,
    videoPayloadMediaKey,
} from 'components/answers/Answer/payloadMediaIdentity'
import { registerMediaTrack } from 'state/mediaPlaybackCoordinator'

// Treechat Radio: one live station broadcasting the most upvalued songs
// (audio, or native video — never YouTube). The
// server owns the schedule (a deterministic function of wall-clock time), so
// every listener hears the same song at the same position; clients fetch
// /radio/now, seek to the live offset, and re-sync at each track boundary.

// The whole draw, not a leaderboard: the tab lists every song with a chance.
export const RADIO_POOL_LIMIT = 100
const RADIO_POOL_TTL_MS = 5 * 60 * 1000
// Clock-skew/latency adjustment cap when seeking to the live position.
const MAX_LIVE_SKEW_SECONDS = 30

let radioPoolCache = null // { tracks, fetchedAt }
let radioPoolInFlight = null
// In-flight reads by audience and by whether they carry the odds chart.
const radioNowInFlight = new Map()

// A radio song is audio or native video. Keyed exactly like the inline
// payload players so the dock treats a radio play and an inline play of the
// same song as one track.
export function radioTrackMediaKey({ kind, url, attachmentId = null, blobId = null }) {
    const identity = { url, attachmentId: attachmentId || null, blobId: blobId || null }
    return kind === 'video' ? videoPayloadMediaKey(identity) : audioAttachmentMediaKey(identity)
}

export function normalizeRadioTrack(raw) {
    const src = raw?.url
    if (!src) return null

    const kind = raw.kind === 'video' ? 'video' : 'audio'
    const mediaKey = radioTrackMediaKey({
        kind,
        url: src,
        attachmentId: raw.attachment_id,
        blobId: raw.blob_id,
    })
    if (!mediaKey) return null

    const upvalueSats = Number(raw.bsv_upvalue_amt)
    const creditedSats = Number(raw.credited_sats)
    const duration = Number(raw.duration)
    const share = value => {
        const number = Number(value)
        return Number.isFinite(number) && number > 0 ? number : 0
    }
    return {
        mediaKey,
        kind,
        src,
        posterSrc: raw.poster_url || null,
        // The media's identity, so a card can draw the song from the station's
        // payload before the song's post loads (radioSongStandIn).
        attachmentId: raw.attachment_id ? String(raw.attachment_id) : null,
        blobId: raw.blob_id ? String(raw.blob_id) : null,
        contentType: raw.content_type || null,
        title: raw.title || null,
        answerId: raw.answer_id ? String(raw.answer_id) : null,
        questId: raw.quest_id ? String(raw.quest_id) : null,
        duration: Number.isFinite(duration) && duration > 0 ? duration : 0,
        upvalueSats: Number.isFinite(upvalueSats) && upvalueSats > 0 ? Math.round(upvalueSats) : 0,
        // Sats listeners have played-in for this song (all-time attribution
        // credit) — the dynamic-playlist ranking signal.
        creditedSats:
            Number.isFinite(creditedSats) && creditedSats > 0 ? Math.round(creditedSats) : 0,
        playlistCount: Number(raw.playlist_count) || 0,
        // Chance in the next draw (pool reads only): the backed and discovery
        // parts add up to it; null when the payload carries no odds.
        chance: raw.chance == null ? null : share(raw.chance),
        chanceSupport: share(raw.chance_support),
        chanceDiscovery: share(raw.chance_discovery),
    }
}

export function normalizeRadioTracks(payload) {
    const seenKeys = new Set()
    return (Array.isArray(payload?.tracks) ? payload.tracks : [])
        .map(normalizeRadioTrack)
        .filter(track => {
            if (!track || seenKeys.has(track.mediaKey)) return false
            seenKeys.add(track.mediaKey)
            return true
        })
}

// Live selection probabilities; older persisted charts still normalize as sections.
export function normalizeRadioContenders(payload) {
    return (Array.isArray(payload?.contenders) ? payload.contenders : [])
        .map(section => {
            if (!section?.key) return null
            const contenders = (Array.isArray(section.contenders) ? section.contenders : [])
                .map(raw => {
                    const sats = Number(raw?.sats)
                    if (!raw?.answer_id || !Number.isFinite(sats) || sats < 0) return null
                    const upvaluedAt = Number(raw.upvalued_at)
                    const duration = Number(raw.duration)
                    return {
                        answerId: String(raw.answer_id),
                        questId: raw.quest_id ? String(raw.quest_id) : null,
                        title: raw.title || null,
                        sats: Math.round(sats),
                        probability: raw.probability == null ? null : Number(raw.probability),
                        playlistCount: Number(raw.playlist_count) || 0,
                        contributions: raw.contributions || null,
                        upvaluedAt: Number.isFinite(upvaluedAt) ? upvaluedAt : null,
                        // Enough to play the contender from a button (the room's
                        // live card), keyed like the tag the stored reply carries.
                        url: raw.url || null,
                        attachmentId: raw.attachment_id ? String(raw.attachment_id) : null,
                        blobId: raw.blob_id ? String(raw.blob_id) : null,
                        duration: Number.isFinite(duration) && duration > 0 ? duration : null,
                        kind: raw.kind === 'video' ? 'video' : 'audio',
                        posterUrl: raw.poster_url || null,
                    }
                })
                .filter(Boolean)
            if (contenders.length === 0) return null
            return {
                key: String(section.key),
                label: section.label || section.key,
                contenders,
                shares: section.shares || null,
                candidateCount: Number(section.candidate_count) || contenders.length,
            }
        })
        .filter(Boolean)
}

// Parses a /radio/now payload into normalized tracks plus broadcast timing.
export function normalizeRadioNow(payload) {
    if (!payload) return null

    const nowTrack = normalizeRadioTrack(payload.now)
    const nextTrack = normalizeRadioTrack(payload.next)
    const serverNow = Number(payload.server_now)
    const position = Number(payload.now?.position)
    const endsAt = Number(payload.now?.ends_at)
    const nowDuration = Number(payload.now?.duration)

    return {
        serverNow: Number.isFinite(serverNow) ? serverNow : null,
        // Local receipt time (client clock), for measuring elapsed airtime
        // without comparing against server timestamps.
        receivedAtMs: Date.now(),
        now: nowTrack
            ? {
                  track: nowTrack,
                  position: Number.isFinite(position) && position > 0 ? position : 0,
                  duration: Number.isFinite(nowDuration) && nowDuration > 0 ? nowDuration : 0,
                  endsAt: Number.isFinite(endsAt) ? endsAt : null,
                  playIndex: Number.isFinite(Number(payload.now?.play_index))
                      ? Number(payload.now.play_index)
                      : null,
                  weightSource: payload.now?.weight_source || null,
                  // Odds the song won its draw at (0..1), and the block that seeded it.
                  chance:
                      payload.now?.chance != null && Number.isFinite(Number(payload.now.chance))
                          ? Number(payload.now.chance)
                          : null,
                  blockHeight:
                      payload.now?.block_height != null &&
                      Number.isFinite(Number(payload.now.block_height))
                          ? Number(payload.now.block_height)
                          : null,
                  playQuestId: payload.now?.play_quest_id
                      ? String(payload.now.play_quest_id)
                      : null,
                  playPostAnswerId: payload.now?.play_post_answer_id
                      ? String(payload.now.play_post_answer_id)
                      : null,
                  author: payload.now?.author || null,
              }
            : null,
        next: nextTrack ? { track: nextTrack } : null,
        contenders: normalizeRadioContenders(payload),
        // False for a read made without the chart (contenders=0), so the feed
        // keeps the chart it already holds for the same play.
        contendersIncluded: Array.isArray(payload.contenders),
    }
}

// Where to seek so playback lines up with the broadcast: the server-reported
// position plus however long the response took to reach us (clamped — a wildly
// skewed client clock should not fling the seek around), kept just short of
// the track end.
/**
 * @param {{ position?: number | null, serverNow?: number | null, duration?: number | null }} live
 * @param {number} [clientNowMs]
 */
export function liveRadioPosition({ position, serverNow, duration }, clientNowMs = Date.now()) {
    const base = Number(position)
    const startPosition = Number.isFinite(base) && base > 0 ? base : 0

    const serverEpoch = Number(serverNow)
    const skew = Number.isFinite(serverEpoch) ? clientNowMs / 1000 - serverEpoch : 0
    const adjusted = startPosition + Math.min(Math.max(skew, 0), MAX_LIVE_SKEW_SECONDS)

    const trackDuration = Number(duration)
    if (Number.isFinite(trackDuration) && trackDuration > 1) {
        return Math.min(adjusted, trackDuration - 0.5)
    }
    return adjusted
}

// The station refuses an early finish report for an unmeasured track until the
// play has been on air this long (Radio::AdvanceService::MIN_UNMEASURED_PLAY_SECONDS).
const RADIO_MIN_UNMEASURED_PLAY_SECONDS = 20
const RADIO_FINISH_RETRY_MARGIN_MS = 500

// How long to wait before re-sending a refused finish report: until the play's
// minimum airtime has passed. Airtime now is the server position plus the time
// since this client received it, both measured on the client's own clock
// (receivedAtMs), so a skewed client clock cannot move the retry earlier than
// the station's floor.
/**
 * @param {{ position?: number | null, receivedAtMs?: number | null }} live
 * @param {number} [clientNowMs] same clock as receivedAtMs
 */
export function radioFinishRetryDelayMs({ position, receivedAtMs }, clientNowMs = Date.now()) {
    const base = position == null ? NaN : Number(position)
    const received = receivedAtMs == null ? NaN : Number(receivedAtMs)
    const maxDelay = RADIO_MIN_UNMEASURED_PLAY_SECONDS * 1000 + RADIO_FINISH_RETRY_MARGIN_MS
    if (!Number.isFinite(base) || !Number.isFinite(received)) return maxDelay

    const elapsedMs = Math.max(base, 0) * 1000 + Math.max(clientNowMs - received, 0)
    const remaining = RADIO_MIN_UNMEASURED_PLAY_SECONDS * 1000 - elapsedMs
    return Math.min(Math.max(remaining, 0) + RADIO_FINISH_RETRY_MARGIN_MS, maxDelay)
}

function radioTracksUrl() {
    const gonUrl = typeof globalThis !== 'undefined' ? globalThis.gon?.api?.radioTracks : null
    return gonUrl || '/api/v1/radio/tracks'
}

function radioNowUrl() {
    const gonUrl = typeof globalThis !== 'undefined' ? globalThis.gon?.api?.radioNow : null
    return gonUrl || '/api/v1/radio/now'
}

function radioFinishedUrl() {
    const gonUrl = typeof globalThis !== 'undefined' ? globalThis.gon?.api?.radioFinished : null
    return gonUrl || '/api/v1/radio/finished'
}

const RADIO_LISTENER_ID_KEY = 'treechatRadioListenerId'
let radioListenerId = null

// Reads are anonymous, so the station counts listeners by a random id kept
// per browser: tabs share it, and signed-out listeners count too.
function getRadioListenerId() {
    if (radioListenerId) return radioListenerId
    try {
        radioListenerId = localStorage.getItem(RADIO_LISTENER_ID_KEY)
    } catch {}
    if (!radioListenerId) {
        radioListenerId =
            globalThis.crypto?.randomUUID?.() ||
            `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
        try {
            localStorage.setItem(RADIO_LISTENER_ID_KEY, radioListenerId)
        } catch {}
    }
    return radioListenerId
}

function listeningRadioNowUrl() {
    const url = radioNowUrl()
    const listenerId = encodeURIComponent(getRadioListenerId())
    return `${url}${url.includes('?') ? '&' : '?'}listening=1&listener_id=${listenerId}`
}

function registerRadioTracks(tracks) {
    tracks.forEach(track => {
        registerMediaTrack(track.mediaKey, {
            kind: track.kind,
            src: track.src,
            // Only a known poster: never clobber one an inline player registered.
            ...(track.posterSrc ? { posterSrc: track.posterSrc } : {}),
            title: track.title,
            answerId: track.answerId,
            questId: track.questId,
        })
    })
}

// Resolves to the current broadcast ({serverNow, now, next, contenders}) or
// null, never rejects. Never cached — a stale "now" defeats the point of live
// sync — but concurrent callers in the same listening mode share one request.
// Registers the tracks so the dock can adopt them. Pass {listening: false} for
// informational reads (the contenders panel) so they do not count as a listener.
export function fetchRadioNow(options = {}) {
    const listening = options.listening !== false
    // Cards that only show the song on air skip the chart (contenders: false);
    // they may share a full read already on its way, never the other way round.
    const contenders = options.contenders !== false
    const fullKey = `${listening}:full`
    const key = `${listening}:${contenders ? 'full' : 'light'}`
    const inFlight =
        radioNowInFlight.get(key) || (!contenders ? radioNowInFlight.get(fullKey) : null)
    if (inFlight) {
        // A socket tick can arrive after the server read but before its old
        // response reaches us. Read again after that request settles.
        return options.fresh
            ? inFlight.then(() => fetchRadioNow({ ...options, fresh: false }))
            : inFlight
    }

    const base = listening ? listeningRadioNowUrl() : radioNowUrl()
    const url = contenders ? base : `${base}${base.includes('?') ? '&' : '?'}contenders=0`
    const request = globalThis
        .fetch(url, {
            method: 'GET',
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
        })
        .then(response => {
            if (!response?.ok || typeof response.json !== 'function') return null
            return response.json().catch(() => null)
        })
        .then(data => {
            const live = normalizeRadioNow(data)
            registerRadioTracks([live?.now?.track, live?.next?.track].filter(Boolean))
            return live
        })
        .catch(() => null)
        .finally(() => {
            radioNowInFlight.delete(key)
        })

    radioNowInFlight.set(key, request)
    return request
}

// Tells the station the current song actually reached its end, and resolves to
// the broadcast that follows — same shape as fetchRadioNow.
//
// Slots are scheduled from stored track durations, which are missing for audio
// the analyzer never measured; those tracks get a padded fallback slot and the
// station would otherwise sit in dead air until it expires. Reporting the real
// end switches every listener at once and teaches the station the true length,
// so the next airing is scheduled correctly. The server only ever shortens the
// live slot, and ignores a report for any play that is not on the air.
//
// Falls back to a plain read whenever the report cannot be made or lands too
// late, so a failure degrades to the old hold-until-slot-end behavior.
export function finishRadioNow({ playIndex = null, duration = null } = {}) {
    // No play to report against: read the broadcast instead. Checked before
    // coercion — Number(null) is 0, a real (and wrong) index.
    if (playIndex == null) return fetchRadioNow()
    const index = Number(playIndex)
    if (!Number.isFinite(index)) return fetchRadioNow()

    const observed = Number(duration)
    const body = { play_index: index }
    if (Number.isFinite(observed) && observed > 0) body.duration = observed

    return globalThis
        .fetch(radioFinishedUrl(), {
            method: 'POST',
            credentials: 'same-origin',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        })
        .then(response => {
            if (!response?.ok || typeof response.json !== 'function') return null
            return response.json().catch(() => null)
        })
        .then(data => {
            const live = normalizeRadioNow(data)
            if (!live?.now?.track) return fetchRadioNow()

            registerRadioTracks([live.now.track, live.next?.track].filter(Boolean))
            return live
        })
        .catch(() => fetchRadioNow())
}

// The next draw's shares across the whole pool ({support, discovery,
// discoveryCount}), or null when the payload carries none.
export function normalizeRadioOdds(payload) {
    const odds = payload?.odds
    if (!odds) return null
    const share = value => {
        const number = Number(value)
        return Number.isFinite(number) && number > 0 ? number : 0
    }
    return {
        support: share(odds.support),
        discovery: share(odds.discovery),
        discoveryCount: Math.round(share(odds.discovery_count)),
    }
}

// The odds that came with the cached pool.
export function getRadioPoolOdds() {
    return radioPoolCache?.odds || null
}

// Resolves to the ranked pool (possibly empty), never rejects. Registers
// every pool track with the playback coordinator so the dock can adopt them.
export function fetchRadioPool(options = {}) {
    const now = Date.now()
    if (!options.force && radioPoolCache && now - radioPoolCache.fetchedAt < RADIO_POOL_TTL_MS) {
        return Promise.resolve(radioPoolCache.tracks)
    }
    if (radioPoolInFlight) return radioPoolInFlight

    radioPoolInFlight = globalThis
        .fetch(`${radioTracksUrl()}?limit=${RADIO_POOL_LIMIT}`, {
            method: 'GET',
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
        })
        .then(response => {
            if (!response?.ok || typeof response.json !== 'function') return null
            return response.json().catch(() => null)
        })
        .then(data => {
            if (!data) return radioPoolCache?.tracks || []

            const tracks = normalizeRadioTracks(data)
            registerRadioTracks(tracks)
            radioPoolCache = { tracks, odds: normalizeRadioOdds(data), fetchedAt: Date.now() }
            return tracks
        })
        .catch(() => radioPoolCache?.tracks || [])
        .finally(() => {
            radioPoolInFlight = null
        })

    return radioPoolInFlight
}
